/**
 * PortalSwitcher — persistent inter-portal navigation card.
 *
 * Doctrine: `/app/memory/FINAL_PLATFORM_STRUCTURE.md` §22:
 *   "Users do NOT need to return to the gateway. The corner card/arrow
 *    becomes a portal handoff system that switches between Customer /
 *    Provider / Tools."
 *
 * Behavior:
 *   - Renders in the top-right of any portal landing page.
 *   - Shows a mini preview card of one of the OTHER two portals.
 *   - Arrow tap → cycles to the next OTHER portal (preview only — does NOT
 *     navigate yet).
 *   - Tapping the preview card itself navigates into that portal.
 *
 * Each portal is its own identity (color, icon, footer color):
 *   Customer  → white body + purple icon/footer
 *   Provider  → blue body + dark footer
 *   Tools     → bright orange body + presidential-blue icon/footer/border
 */
import React, { useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Briefcase, Wrench, Network, ChevronRight } from 'lucide-react';
import { HOMEPAGE_SHOW_NC_CARD, HOMEPAGE_SHOW_TOOLS_CARD } from '../config/launchFlags';

const PORTALS = {
  customer: {
    id: 'customer',
    label: 'Customer',
    route: '/remodify',
    icon: Users,
    // White body · purple icon/footer/text · purple border
    bodyStyle: { background: '#FFFFFF' },
    iconColor: '#7C3AED',
    accentBars: ['#E2E8F0', '#F1F5F9'],
    footerStyle: { background: '#FFFFFF', borderTop: '1px solid #C4B5FD' },
    footerTextColor: '#6D28D9',
    borderColor: 'rgba(196,181,253,0.7)',
    hoverBorderColor: 'rgba(124,58,237,0.85)',
  },
  provider: {
    id: 'provider',
    label: 'Provider',
    route: '/landing',
    icon: Briefcase,
    // Blue body · slate-900 footer · light text
    bodyStyle: { backgroundImage: 'linear-gradient(to right, #2563EB 0%, #1D4ED8 50%, #1E40AF 100%)' },
    iconColor: '#FFFFFF',
    accentBars: ['rgba(147,197,253,0.85)', 'rgba(191,219,254,0.85)'],
    footerStyle: { background: '#0F172A' },
    footerTextColor: '#F1F5F9',
    borderColor: 'rgba(96,165,250,0.7)',
    hoverBorderColor: 'rgba(37,99,235,0.95)',
  },
  network_contractor: {
    id: 'network_contractor',
    label: 'Network',
    route: '/nc-landing',
    icon: Network,
    // Emerald body · white icon · dark-emerald footer/text · emerald border
    bodyStyle: { backgroundImage: 'linear-gradient(to right, #10B981 0%, #059669 50%, #047857 100%)' },
    iconColor: '#FFFFFF',
    accentBars: ['rgba(167,243,208,0.85)', 'rgba(110,231,183,0.55)'],
    footerStyle: { background: '#022C22' },
    footerTextColor: '#A7F3D0',
    borderColor: 'rgba(52,211,153,0.7)',
    hoverBorderColor: 'rgba(5,150,105,0.95)',
  },
  tools: {
    id: 'tools',
    label: 'Tools',
    route: '/tools',
    icon: Wrench,
    // Bright orange body · presidential-blue footer/icon/text/border
    bodyStyle: { backgroundImage: 'linear-gradient(135deg, #FB923C 0%, #F97316 50%, #EA580C 100%)' },
    iconColor: '#1D4ED8',
    accentBars: ['rgba(255,255,255,0.85)', 'rgba(255,255,255,0.55)'],
    footerStyle: { background: '#FFFFFF', borderTop: '1px solid #2563EB' },
    footerTextColor: '#1D4ED8',
    borderColor: '#2563EB',
    hoverBorderColor: '#1E3A8A',
  },
};

// Launch focus (owner directive): the public switcher cycles Customer ⇄
// Provider only. Network Contractor and Tools re-enter the cycle when their
// launch flags flip — nothing behind them is removed.
const ORDER = [
  'customer',
  'provider',
  ...(HOMEPAGE_SHOW_NC_CARD ? ['network_contractor'] : []),
  ...(HOMEPAGE_SHOW_TOOLS_CARD ? ['tools'] : []),
];

// Arrow color per "current portal" — primary identity color of the page
// the user is on. Provider page = blue arrow (matches sign-up CTA),
// Customer page = purple arrow (matches Request Service CTA), NC page =
// emerald arrow (matches NC tile / signup), Tools page = orange arrow
// (matches Tools CTAs).
const ARROW_COLOR_BY_PORTAL = {
  customer:           { bg: 'rgba(124, 58, 237, 0.15)', border: 'rgba(124, 58, 237, 0.4)', icon: '#7C3AED', ring: 'rgba(124,58,237,0.5)' },
  provider:           { bg: 'rgba(37, 99, 235, 0.15)',  border: 'rgba(37, 99, 235, 0.4)',  icon: '#2563EB', ring: 'rgba(37,99,235,0.5)' },
  network_contractor: { bg: 'rgba(16, 185, 129, 0.15)', border: 'rgba(16, 185, 129, 0.4)', icon: '#10B981', ring: 'rgba(16,185,129,0.5)' },
  tools:              { bg: 'rgba(249, 115, 22, 0.15)', border: 'rgba(249, 115, 22, 0.4)', icon: '#F97316', ring: 'rgba(249,115,22,0.5)' },
};

/**
 * @param {{ currentPortal: 'customer'|'provider'|'tools', className?: string }} props
 */
export default function PortalSwitcher({ currentPortal, className = '' }) {
  const navigate = useNavigate();

  const others = useMemo(() => ORDER.filter((p) => p !== currentPortal), [currentPortal]);
  const [shownIdx, setShownIdx] = useState(0);

  const shown = PORTALS[others[shownIdx % others.length]];
  const Icon = shown.icon;
  const arrowTheme = ARROW_COLOR_BY_PORTAL[currentPortal] || ARROW_COLOR_BY_PORTAL.provider;

  const handleCycle = useCallback((e) => {
    e.stopPropagation();
    setShownIdx((i) => (i + 1) % others.length);
  }, [others.length]);

  // Mobile swipe-to-cycle. Tracks horizontal touch deltas across the
  // whole switcher region; swipe of >40px (left or right) cycles the
  // preview the same way a tap on the arrow does. Vertical swipes are
  // ignored so the parent page can still scroll naturally.
  const touchRef = React.useRef({ x: 0, y: 0, t: 0, dx: 0, dy: 0, active: false });
  const onTouchStart = useCallback((e) => {
    const t = e.touches?.[0];
    if (!t) return;
    touchRef.current = { x: t.clientX, y: t.clientY, t: Date.now(), dx: 0, dy: 0, active: true };
  }, []);
  const onTouchMove = useCallback((e) => {
    if (!touchRef.current.active) return;
    const t = e.touches?.[0];
    if (!t) return;
    touchRef.current.dx = t.clientX - touchRef.current.x;
    touchRef.current.dy = t.clientY - touchRef.current.y;
  }, []);
  const onTouchEnd = useCallback(() => {
    const { dx, dy, active } = touchRef.current;
    touchRef.current.active = false;
    if (!active) return;
    if (Math.abs(dx) >= 40 && Math.abs(dx) > Math.abs(dy)) {
      setShownIdx((i) => (i + 1) % others.length);
    }
  }, [others.length]);

  const handleSwitch = useCallback(() => {
    navigate(shown.route);
  }, [navigate, shown.route]);

  return (
    <div
      className={`flex items-stretch gap-1.5 ${className}`}
      data-testid="portal-switcher"
      data-current-portal={currentPortal}
      data-shown-portal={shown.id}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Mini preview card — tap to switch into that portal */}
      <button
        onClick={handleSwitch}
        type="button"
        className="group rounded-xl overflow-hidden border-2 transition-all duration-300 hover:scale-[1.04] hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-1"
        style={{
          width: '88px',
          borderColor: shown.borderColor,
        }}
        data-testid={`portal-switch-to-${shown.id}`}
        aria-label={`Switch to ${shown.label}`}
      >
        <div
          className="h-10 flex items-center justify-center relative"
          style={shown.bodyStyle}
        >
          <Icon className="w-4 h-4 group-hover:scale-110 transition-transform" style={{ color: shown.iconColor }} />
          <div className="absolute bottom-1 left-1.5 right-1.5">
            <div className="h-0.5 rounded mb-0.5 w-3/4" style={{ background: shown.accentBars[0] }} />
            <div className="h-0.5 rounded w-1/2" style={{ background: shown.accentBars[1] }} />
          </div>
        </div>
        <div className="py-1 px-1.5 text-center" style={shown.footerStyle}>
          <p className="font-semibold text-[10px]" style={{ color: shown.footerTextColor }}>
            {shown.label}
          </p>
        </div>
      </button>

      {/* Cycle arrow — colored to match the CURRENT portal's primary identity */}
      <button
        onClick={handleCycle}
        type="button"
        className="rounded-xl border-2 backdrop-blur transition-all flex items-center justify-center px-1.5 focus:outline-none focus:ring-2"
        style={{
          width: '32px',
          background: arrowTheme.bg,
          borderColor: arrowTheme.border,
        }}
        data-testid="portal-switcher-cycle"
        aria-label="Cycle to next portal"
      >
        <ChevronRight className="w-4 h-4" style={{ color: arrowTheme.icon }} />
      </button>
    </div>
  );
}
